/**
 * Project Terminology Composable
 * 
 * Provides dynamic terminology based on project configuration.
 * Different projects can call their entities different things:
 * - Construction: "Subcontractors" and "Sites"
 * - Franchise: "Franchisees" and "Stores"
 * - Legal: "Law Firms" and "Offices"
 * - etc.
 */

import { computed } from 'vue';

export function useProjectTerminology(project) {
  const entityTerm = computed(() => {
    if (!project || !project.value) {
      return { singular: 'Company', plural: 'Companies' };
    }
    
    const proj = project.value;
    
    // Custom terminology
    if (proj.entity_terminology === 'custom' && proj.entity_term_singular) {
      return {
        singular: proj.entity_term_singular,
        plural: proj.entity_term_plural || `${proj.entity_term_singular}s`
      };
    }
    
    // If API already provided the terms (from serializer)
    if (proj.entity_term) {
      return proj.entity_term;
    }
    
    // Fallback to predefined terms
    const terms = {
      subcontractors: { singular: 'Subcontractor', plural: 'Subcontractors' },
      vendors: { singular: 'Vendor', plural: 'Vendors' },
      franchisees: { singular: 'Franchisee', plural: 'Franchisees' },
      law_firms: { singular: 'Law Firm', plural: 'Law Firms' },
      suppliers: { singular: 'Supplier', plural: 'Suppliers' },
      partners: { singular: 'Partner', plural: 'Partners' },
      patients: { singular: 'Patient', plural: 'Patients' },
      companies: { singular: 'Company', plural: 'Companies' }
    };
    
    return terms[proj.entity_terminology] || terms.companies;
  });
  
  const locationTerm = computed(() => {
    if (!project || !project.value) {
      return { singular: 'Location', plural: 'Locations' };
    }
    
    const proj = project.value;
    
    // Custom terminology
    if (proj.location_terminology === 'custom' && proj.location_term_custom) {
      return {
        singular: proj.location_term_custom,
        plural: `${proj.location_term_custom}s`
      };
    }
    
    // If API already provided the terms (from serializer)
    if (proj.location_term) {
      return proj.location_term;
    }
    
    // Fallback to predefined terms
    const terms = {
      locations: { singular: 'Location', plural: 'Locations' },
      stores: { singular: 'Store', plural: 'Stores' },
      sites: { singular: 'Site', plural: 'Sites' },
      jobsites: { singular: 'Job site', plural: 'Job sites' },
      houses: { singular: 'House', plural: 'Houses' },
      buildings: { singular: 'Building', plural: 'Buildings' },
      offices: { singular: 'Office', plural: 'Offices' },
      centers: { singular: 'Center', plural: 'Centers' },
      branches: { singular: 'Branch', plural: 'Branches' }
    };
    
    return terms[proj.location_terminology] || terms.locations;
  });
  
  return {
    entityTerm,
    locationTerm
  };
}

/**
 * Get entity terminology options for settings dropdown
 */
export const ENTITY_TERMINOLOGY_OPTIONS = [
  { value: 'subcontractors', label: 'Subcontractors', example: 'Construction, General Contractors' },
  { value: 'vendors', label: 'Vendors', example: 'Supply Chain, Procurement' },
  { value: 'franchisees', label: 'Franchisees', example: 'Franchise Systems' },
  { value: 'law_firms', label: 'Law Firms', example: 'Legal Networks' },
  { value: 'suppliers', label: 'Suppliers', example: 'Manufacturing, Retail' },
  { value: 'partners', label: 'Partners', example: 'Business Partnerships' },
  { value: 'patients', label: 'Patients', example: 'Veterinary Clinical Practice' },
  { value: 'companies', label: 'Companies', example: 'General Use' },
  { value: 'custom', label: 'Custom...', example: 'Define your own' }
];

/**
 * Get location terminology options for settings dropdown
 */
export const LOCATION_TERMINOLOGY_OPTIONS = [
  { value: 'locations', label: 'Locations' },
  { value: 'stores', label: 'Stores' },
  { value: 'sites', label: 'Sites' },
  { value: 'jobsites', label: 'Job sites' },
  { value: 'houses', label: 'Houses' },
  { value: 'buildings', label: 'Buildings' },
  { value: 'offices', label: 'Offices' },
  { value: 'centers', label: 'Centers' },
  { value: 'branches', label: 'Branches' },
  { value: 'custom', label: 'Custom...' }
];

